import { requestTypes } from "./constants";
import { SECTION_IDS } from "./v2/sectionConfig";

/**
 * Purpose-built variants of the V2 request flow.
 *
 * A variant is a PURELY FRONTEND overlay on an existing request type. The URL
 * carries the variant (`/request/record-sale`), but `requestType` is normalised
 * to a canonical value (`order` / `sell`) the moment it enters state, so every
 * existing predicate, API payload and section gate keeps working untouched.
 * That is what makes these safe to add: the backend only ever accepts
 * `request_type: order|sell` (see useRequest's saveRequest), and the ~2k-line
 * state machine branches exclusively on the canonical value.
 *
 * What a variant declares:
 *   urlType         — the URL segment
 *   requestType     — canonical type the flow behaves as
 *   title           — page title (topbar)
 *   searchDefault   — default SEARCH PRODUCT source tab for the PRIMARY slot
 *                     ('catalog' = Database, 'listing' = Active Listings)
 *   extraTabs       — source tabs to expose beyond the defaults for the slot
 *                     kind (e.g. Previous Purchases on a primary sell slot)
 *   confirmLabels   — overrides for the CONFIRM REQUEST button row. Keys are
 *                     the existing action options, so the handlers are
 *                     unchanged: `finish` / `add` / `add_main`. `addMainAs`
 *                     duplicates `add_main` under a second label whose new
 *                     slot opens a different default tab.
 *   sectionLabels   — per-section label overrides (left rail + accordion)
 *   sectionFirst    — section ids hoisted to the front of the flow, in order.
 *                     All three variants put ASSIGN CLIENT first: the rep starts
 *                     from the person, not the watch.
 *   autoSkip(state) — section ids to drop from the flow (the ONLY true skip
 *                     mechanism: a hidden section is stepped over by
 *                     onAdvance, and getSectionStatus never sees it)
 *   pxExtras        — for a PX order, also show the sell-side sections
 *                     (agreements / PO / publish) after INVOICE
 */

export const FLOW_VARIANTS = {
  record_sale: {
    key: "record_sale",
    urlType: "record-sale",
    requestType: requestTypes.ORDER,
    title: "Record a Sale",
    searchDefault: "listing",
    // "Assign Client; Search Product defaulting to Active Listings…" — the spec
    // orders it that way, and a sale starts from the buyer in front of you.
    sectionFirst: [SECTION_IDS.ASSIGN_CLIENT],
    confirmLabels: {
      finish: "Finalise Order",
      add: "Add Trade-In",
      add_main: "Add Listing",
      // Same add_main handler; the new slot's SEARCH opens on Database
      // instead of Active Listings.
      addMainAs: { label: "Source Product", searchDefault: "catalog" },
    },
    sectionLabels: {
      confirm_request: "Confirm Request",
      send_quote: "Select Quote",
    },
    // CONFIRM REQUEST stays: it is where the four actions live (Finalise Order /
    // Add Listing / Add Trade-In / Source Product), so skipping it left the flow
    // with no way to finalise, add a second product or start a trade-in. It is
    // auto-populated from the listing (price, year, condition — see
    // V2SearchProduct's Active-Listings prefill), so landing on it costs the rep
    // nothing but a glance.
    //
    // SELECT QUOTE is still dropped: a listing-sourced sale IS priced by its
    // listing. The data-driven rule in sectionConfig now covers the
    // linked_listing_id case on its own; this keeps the `listing?.id` entry point
    // (a sale started FROM a listing, where the request may not carry the pointer
    // yet) behaving the same way.
    autoSkip: ({ listing, request }) =>
      listing?.id || request?.linked_listing_id ? ["send_quote"] : [],
  },

  sourcing_enquiry: {
    key: "sourcing_enquiry",
    urlType: "sourcing",
    requestType: requestTypes.ORDER,
    title: "Sourcing Enquiry",
    searchDefault: "catalog",
    sectionFirst: [SECTION_IDS.ASSIGN_CLIENT],
    confirmLabels: {
      finish: "Place Order",
      add: "Add Trade-In / PX",
      add_main: "Add More Order",
    },
    sectionLabels: {
      confirm_request: "Request Details",
    },
    autoSkip: () => [],
  },

  sell_enquiry: {
    key: "sell_enquiry",
    urlType: "sell-enquiry",
    requestType: requestTypes.SELL,
    title: "Sell Enquiry",
    searchDefault: "catalog",
    sectionFirst: [SECTION_IDS.ASSIGN_CLIENT],
    // The standalone sell flow deliberately hides Previous Purchases on its
    // primary slot; this variant asks for it back (spec: "Other tab: Previous
    // purchases"). Scoped to the variant so /request/sell is unchanged.
    extraTabs: ["paid_cart"],
    confirmLabels: {
      finish: "Place Request",
      add: "Add Trade-In / PX",
      add_main: "Add More Sell",
    },
    sectionLabels: {
      confirm_request: "Request Details",
    },
    autoSkip: () => [],
  },
};

// NOTE on the PX sell-side sections (Sell Payments / the two agreements / PO /
// Publish Listing shown after INVOICE, one set per trade-in): that is NOT a
// variant flag. It is driven by the deal's own data — see
// getDynamicPxSellExtraSections in v2/sectionConfig.js — because a PX order is a
// PX order whichever flow started it, and an order-side variant hands its URL
// back to the px-order contract as soon as a trade-in is added.

const BY_URL = Object.fromEntries(
  Object.values(FLOW_VARIANTS).map((v) => [v.urlType, v])
);

/** Variant descriptor for a raw URL segment, or null for the stock flows. */
export const flowVariantFor = (urlType) => BY_URL[urlType] ?? null;

/**
 * Canonical request type for a raw URL segment. Unknown segments fall through
 * unchanged so the existing types (order / sell / stock / px-order / px-cart)
 * and any future ones behave exactly as before.
 */
export const canonicalRequestType = (urlType) =>
  BY_URL[urlType]?.requestType ?? urlType;

/** Variant-aware page title, or null to fall back to the default map. */
export const flowVariantTitle = (urlType) => BY_URL[urlType]?.title ?? null;
